import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import {
  acceptAnalytics,
  getConsent,
  onConsentChange,
  rejectAnalytics,
  type ConsentState,
} from "@/lib/consent";

/**
 * First-visit banner. Hidden once the user has made a choice or if GPC is
 * asserted (in which case the rejection is automatic and silent — surfacing
 * a banner would be redundant per CPRA § 1798.135(b)).
 *
 * Never rendered in the native app: Apple App Review 5.1.2(i) treats an
 * in-app cookie prompt as implied tracking. getConsent() already locks native
 * to "denied", but we also hard-guard here so the prompt can never surface.
 */
export function CookieBanner() {
  const [consent, setConsent] = useState<ConsentState | null>(null);

  useEffect(() => {
    setConsent(getConsent());
    return onConsentChange(setConsent);
  }, []);

  if (Capacitor.isNativePlatform()) return null;
  if (!consent) return null;
  if (consent.gpc) return null;
  if (consent.analytics !== "unset") return null;

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground md:max-w-3xl">
          DashAlign uses cookies to keep you signed in and, with your permission,
          to measure how the site is used. You can change your choice anytime via
          {" "}
          <Link to="/do-not-sell" className="underline hover:text-foreground">
            Your Privacy Choices
          </Link>
          . See our{" "}
          <Link to="/privacy-policy" className="underline hover:text-foreground">
            Privacy Policy
          </Link>
          .
        </p>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => rejectAnalytics()}
          >
            Reject non-essential
          </Button>
          <Button
            size="sm"
            onClick={() => acceptAnalytics()}
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
